import { useState } from 'react';
import { Check, AlertTriangle, XCircle, Package, ShoppingCart } from 'lucide-react';
import { useToast } from '../../../components/ui/Toast';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Card } from '../../../components/ui/Card';
import { Divider } from '../../../components/ui/Divider';
import { Alert } from '../../../components/ui/Alert';
import { Loader } from '../../../components/ui/Loader';
import { Skeleton } from '../../../components/ui/Skeleton';
import { StatusIndicator } from '../../../components/ui/StatusIndicator';
import { Table, TableColumn, SortDirection } from '../../../components/ui/Table';
import { DataList } from '../../../components/ui/DataList';
import { Checkbox } from '../../../components/ui/Checkbox';
import { EmptyState } from '../../../components/EmptyState';
import { CodeSnippet } from '../components/CodeSnippet';
import { Subsection } from '../components/Subsection';
import { ProgressBar } from '../../../components/ui/ProgressBar';
import { CollapsibleBanner } from '../../../components/ui/CollapsibleBanner';
import { Bot, Sparkles } from 'lucide-react';

// ============================================================================
// Sekcja: Badge
// ============================================================================

export function BadgeSection() {
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Komponent <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Badge>'}</code>.
        Warianty: neutral / success / warning / danger / info / accent. Rozmiary: sm / md.
        Opcjonalny <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">leftIcon</code>.
      </p>

      <Subsection n={1} title="Warianty">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 flex flex-wrap gap-2">
          <Badge variant="neutral">Neutral</Badge>
          <Badge variant="success">Zapłacone</Badge>
          <Badge variant="warning">Oczekuje</Badge>
          <Badge variant="danger">Anulowane</Badge>
          <Badge variant="info">Info</Badge>
          <Badge variant="accent">Nowe</Badge>
        </div>
      </Subsection>

      <Subsection n={2} title="Rozmiary">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 flex flex-wrap items-center gap-3">
          <Badge variant="success" size="sm">sm</Badge>
          <Badge variant="success" size="md">md (default)</Badge>
          <Badge variant="accent" size="sm" leftIcon={<Check />}>Done</Badge>
          <Badge variant="accent" size="md" leftIcon={<Check />}>Done</Badge>
        </div>
      </Subsection>

      <Subsection n={3} title="Z ikonami (przykłady biznesowe)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 flex flex-wrap gap-2">
          <Badge variant="success" leftIcon={<Check />}>Dostarczone</Badge>
          <Badge variant="warning" leftIcon={<AlertTriangle />}>Wstrzymane</Badge>
          <Badge variant="danger" leftIcon={<XCircle />}>Zwrot</Badge>
          <Badge variant="info" leftIcon={<Package />}>W drodze</Badge>
          <Badge variant="accent" leftIcon={<ShoppingCart />}>Koszyk</Badge>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Feedback (Alert / Toast / Loader / Skeleton / StatusIndicator / EmptyState)
// ============================================================================

export function FeedbackSection() {
  const toast = useToast();
  const [alertVisible, setAlertVisible] = useState(true);

  return (
    <div className="space-y-10">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Komponenty informacji zwrotnej: <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Alert>'}</code>{' '}
        (inline banner), <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Toast>'}</code>{' '}
        (auto-hide), <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Loader>'}</code>,{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Skeleton>'}</code>,{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<StatusIndicator>'}</code>, plus istniejący{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<EmptyState>'}</code>.
      </p>

      <Subsection n={1} title="Alert (inline banner)">
        <div className="space-y-3">
          <Alert variant="success" title="Zapisano zmiany">
            Twoje ustawienia zostały zapisane. Synchronizacja do WooCommerce w toku.
          </Alert>
          <Alert variant="warning" title="Uwaga">
            InPost API zwraca wolniejsze odpowiedzi. Sync może potrwać dłużej niż zwykle.
          </Alert>
          <Alert variant="danger" title="Błąd połączenia">
            Nie udało się pobrać zamówień z WooCommerce. Spróbuj ponownie za chwilę.
          </Alert>
          <Alert variant="info">
            Informacja bez tytułu - tylko treść. Dobre dla krótkich kontekstowych komunikatów.
          </Alert>
          {alertVisible && (
            <Alert variant="info" title="Zamykany alert" onClose={() => setAlertVisible(false)}>
              Kliknij X żeby ukryć.
            </Alert>
          )}
          {!alertVisible && (
            <Button size="sm" variant="ghost" onClick={() => setAlertVisible(true)}>
              Pokaż ukryty alert
            </Button>
          )}
        </div>
      </Subsection>

      <Subsection n={2} title="Toast (auto-hide, prawy górny róg)" description={<>Globalny <code className="px-1 rounded bg-surface-raised text-text-secondary">{'useToast()'}</code> hook — {'<ToastProvider>'} wpięte w App.tsx. Auto-hide 4s default (0 = persistent). Kliknij przyciski żeby zobaczyć.</>}>
        <CodeSnippet
          className="mb-3"
          language="tsx"
          code={`import { useToast } from '@/components/ui/Toast';

const toast = useToast();

toast.success('Zapisano');
toast.error('Nie udało się zapisać', 'Błąd');   // tytuł + description
toast.warning('Sesja wygaśnie za 2 min');
toast.info('3 nowe zamówienia');

// persistent (nie znika sam):
toast.push({ variant: 'info', description: '...', duration: 0 });`}
        />
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 flex flex-wrap gap-2">
          <Button size="sm" variant="primary" onClick={() => toast.success('Zapisano pomyślnie')}>
            success
          </Button>
          <Button size="sm" variant="secondary" onClick={() => toast.error('Nie udało się zapisać', 'Błąd')}>
            error (z tytułem)
          </Button>
          <Button size="sm" variant="secondary" onClick={() => toast.warning('Sesja wygaśnie za 2 min')}>
            warning
          </Button>
          <Button size="sm" variant="ghost" onClick={() => toast.info('3 nowe zamówienia')}>
            info
          </Button>
          <Button size="sm" variant="ghost" onClick={() => toast.push({ variant: 'info', title: 'Bez znikania', description: 'duration=0, musisz sam zamknąć', duration: 0 })}>
            persistent
          </Button>
          <Button size="sm" variant="ghost" onClick={() => {
            toast.success('Pierwszy');
            setTimeout(() => toast.info('Drugi'), 200);
            setTimeout(() => toast.warning('Trzeci'), 400);
          }}>
            stack (3 naraz)
          </Button>
        </div>
      </Subsection>

      <Subsection n={3} title="Loader (spinner)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-4">
          <div className="flex items-center gap-5 flex-wrap">
            <Loader size="sm" />
            <Loader size="md" />
            <Loader size="lg" />
            <Loader size="xl" />
          </div>
          <Divider />
          <div className="flex items-center gap-5 flex-wrap">
            <Loader size="sm" label="Wysyłanie..." />
            <Loader size="md" label="Zapisywanie zamówienia..." tone="muted" />
            <Loader size="md" label="Pobieranie danych" />
          </div>
          <Divider />
          <Loader size="lg" label="Ładowanie panelu..." center />
        </div>
      </Subsection>

      <Subsection n={4} title="Skeleton (placeholder)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-5">
          <div>
            <p className="text-xs text-text-muted mb-2">Text (4 linie, ostatnia węższa):</p>
            <Skeleton variant="text" lines={4} />
          </div>
          <Divider />
          <div>
            <p className="text-xs text-text-muted mb-2">Rect + Circle (avatar + name + subtitle):</p>
            <div className="flex items-start gap-3">
              <Skeleton variant="circle" width="48px" height="48px" />
              <div className="flex-1 space-y-2">
                <Skeleton variant="rect" height="16px" width="40%" />
                <Skeleton variant="rect" height="12px" width="60%" />
              </div>
            </div>
          </div>
          <Divider />
          <div>
            <p className="text-xs text-text-muted mb-2">Lista kart (loading state):</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[0, 1, 2].map(i => (
                <div key={i} className="rounded-xl border border-border-subtle bg-surface-raised/30 p-4">
                  <Skeleton variant="rect" height="20px" width="50%" className="mb-3" />
                  <Skeleton variant="text" lines={2} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Subsection>

      <Subsection n={5} title="StatusIndicator (kropka)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-3">
          <div className="flex items-center gap-5 flex-wrap">
            <StatusIndicator tone="online" label="Online" />
            <StatusIndicator tone="idle" label="Idle" />
            <StatusIndicator tone="busy" label="Zajęty" />
            <StatusIndicator tone="warning" label="Ostrzeżenie" />
            <StatusIndicator tone="offline" label="Offline" />
          </div>
          <Divider />
          <div className="flex items-center gap-5 flex-wrap">
            <StatusIndicator tone="online" label="Pulse (LIVE)" pulse />
            <StatusIndicator tone="warning" label="Sync w toku" pulse />
            <StatusIndicator tone="busy" label="Alert" pulse size="sm" />
          </div>
        </div>
      </Subsection>

      <Subsection n={6} title="EmptyState (istniejący komponent)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <EmptyState
            size="sm"
            icon={Package}
            title="Brak zamówień"
            description="Dodaj pierwsze zamówienie, żeby zacząć trackować statusy."
            buttonLabel="Dodaj zamówienie"
            onButtonClick={() => toast.info('Kliknięto z EmptyState')}
          />
        </div>
      </Subsection>

      <Subsection n={7} title="CollapsibleBanner (zwijany notyfikator inline)" description={<>Banner z ikoną + labelem + akcjami po prawej (chevron, opcjonalna ikona kosza) i <b>zwijaną</b> treścią. Klik w całą belkę toggluje. Użyj dla AI draftów, propozycji, wewnętrznych notek.</>}>
        <CodeSnippet
          className="mb-3"
          language="tsx"
          code={`<CollapsibleBanner
  icon={Bot}
  label="Propozycja AI"
  accent="secondary"              // purple dla elementów AI
  defaultCollapsed={false}
  onDelete={() => {}}             // opcjonalne: ikona kosza
  deleteTitle="Usuń draft (nie wróci)"
>
  <p>Treść widoczna po rozwinięciu...</p>
</CollapsibleBanner>`}
        />
        <div className="space-y-3">
          <CollapsibleBanner icon={Bot} label="Propozycja AI (secondary / purple)" accent="secondary" onDelete={() => toast.info('Delete')}>
            <p className="text-sm text-text-primary leading-relaxed">Treść propozycji AI. Domyślnie rozwinięty — klik w belkę lub chevron zwija.</p>
          </CollapsibleBanner>
          <CollapsibleBanner icon={Sparkles} label="Uwaga systemowa (warning)" accent="warning" defaultCollapsed>
            <p className="text-sm text-text-primary leading-relaxed">Collapsible domyślnie zwinięty. Klik żeby rozwinąć.</p>
          </CollapsibleBanner>
          <CollapsibleBanner icon={Bot} label="Primary (accent)" accent="primary">
            <p className="text-sm text-text-primary leading-relaxed">Accent primary — dla większości neutralnych notifierów.</p>
          </CollapsibleBanner>
          <CollapsibleBanner label="Neutral (bez ikony, bez akcji)" accent="neutral">
            <p className="text-sm text-text-primary leading-relaxed">Najprostsza forma.</p>
          </CollapsibleBanner>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Tabele (Table + DataList)
// ============================================================================

interface OrderRow {
  id: string;
  number: string;
  customer: string;
  total: number;
  status: 'paid' | 'pending' | 'cancelled';
  createdAt: string;
}

const ORDER_DATA: OrderRow[] = [
  { id: '1', number: '#1284', customer: 'Anna Nowak', total: 129.9, status: 'paid', createdAt: '2026-04-21' },
  { id: '2', number: '#1283', customer: 'Piotr Kowalski', total: 450.0, status: 'paid', createdAt: '2026-04-21' },
  { id: '3', number: '#1282', customer: 'Marta Wiśniewska', total: 89.5, status: 'pending', createdAt: '2026-04-20' },
  { id: '4', number: '#1281', customer: 'Jan Zieliński', total: 1240.0, status: 'paid', createdAt: '2026-04-20' },
  { id: '5', number: '#1280', customer: 'Katarzyna Dąbrowska', total: 67.9, status: 'cancelled', createdAt: '2026-04-19' },
];

export function TableSection() {
  const [sortKey, setSortKey] = useState<string | undefined>('number');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [loading, setLoading] = useState(false);
  // Selection state dla wariantu "z checkboxami" - poniżej.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const sorted = useMemoSorted(ORDER_DATA, sortKey, sortDirection);
  const allSelected = sorted.length > 0 && sorted.every(r => selectedIds.has(r.id));
  const someSelected = sorted.some(r => selectedIds.has(r.id));

  const toggleRow = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(sorted.map(r => r.id)));
  };

  // Wzorzec selectable: kolumna checkbox jako PIERWSZA, szerokość `40px`,
  // align `center`. KLUCZOWE: opakowanie w `flex items-center justify-center`
  // w render prop - bez tego checkbox przykleja się do lewej krawędzi komórki
  // (patrz screenshot usera 2026-04-22 z OrdersPage). Header = "select all".
  const columnsWithSelection: TableColumn<OrderRow>[] = [
    {
      key: '__select',
      width: '40px',
      align: 'center',
      header: (
        <span className="flex items-center justify-center" onClick={(e) => { e.stopPropagation(); toggleAll(); }}>
          <Checkbox checked={allSelected} aria-label="Zaznacz wszystkie" onChange={toggleAll} onClick={(e) => e.stopPropagation()} />
        </span>
      ),
      render: (row) => (
        <span className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selectedIds.has(row.id)}
            aria-label={`Zaznacz zamówienie ${row.number}`}
            onChange={() => toggleRow(row.id)}
          />
        </span>
      ),
    },
    { key: 'number', header: 'Numer', sortable: true, cellClassName: 'tabular-nums text-xs text-text-primary font-medium', width: '90px' },
    { key: 'customer', header: 'Klient', sortable: true },
    {
      key: 'total',
      header: 'Kwota',
      sortable: true,
      align: 'right',
      cellClassName: 'tabular-nums text-xs',
      render: (r) => `${r.total.toFixed(2)} zł`,
      width: '100px',
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      render: (r) => {
        const map = {
          paid:      { v: 'success' as const, label: 'Opłacone' },
          pending:   { v: 'warning' as const, label: 'Oczekuje' },
          cancelled: { v: 'danger' as const,  label: 'Anulowane' },
        };
        const cfg = map[r.status];
        return <Badge variant={cfg.v} size="sm">{cfg.label}</Badge>;
      },
    },
  ];
  // someSelected wskaźnik (niepełne zaznaczenie) - na razie tylko jako komunikat. Table nie ma built-in indeterminate.
  void someSelected;

  const columns: TableColumn<OrderRow>[] = [
    { key: 'number', header: 'Numer', sortable: true, cellClassName: 'tabular-nums text-xs text-text-primary font-medium', width: '90px' },
    { key: 'customer', header: 'Klient', sortable: true },
    {
      key: 'total',
      header: 'Kwota',
      sortable: true,
      align: 'right',
      cellClassName: 'tabular-nums text-xs',
      render: (r) => `${r.total.toFixed(2)} zł`,
      width: '100px',
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      render: (r) => {
        const map = {
          paid:      { v: 'success' as const, label: 'Opłacone' },
          pending:   { v: 'warning' as const, label: 'Oczekuje' },
          cancelled: { v: 'danger' as const,  label: 'Anulowane' },
        };
        const cfg = map[r.status];
        return <Badge variant={cfg.v} size="sm">{cfg.label}</Badge>;
      },
    },
    { key: 'createdAt', header: 'Utworzono', sortable: true, cellClassName: 'text-text-muted text-xs', width: '110px' },
  ];

  const invoiceItems = [
    { label: 'Numer faktury', value: 'FV/2026/04/0142' },
    { label: 'Wystawiona', value: '21 kwietnia 2026' },
    { label: 'Klient', value: 'Anna Nowak' },
    { label: 'NIP', value: '1234567890', hint: 'VAT-UE aktywny' },
    { label: 'Netto', value: '105,61 zł' },
    { label: 'VAT (23%)', value: '24,29 zł' },
    { label: 'Razem', value: <span className="font-bold text-text-primary">129,90 zł</span> },
  ];

  return (
    <div className="space-y-10">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Table<T>>'}</code>{' '}
        - generyczna prosta tabela z controlled sort, render prop, loading skeleton i empty state.
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px] ml-1">{'<DataList>'}</code>{' '}
        - pary label/value dla details panels / invoice summary.
      </p>

      <Subsection
        n={1}
        title="Tabela (sortable, klikalne wiersze)"
        actions={
          <Button size="sm" variant="secondary" onClick={() => { setLoading(true); setTimeout(() => setLoading(false), 1500); }}>
            Pokaż loading (1.5s)
          </Button>
        }
      >
        <Table
          columns={columns}
          data={sorted}
          getRowKey={(r) => r.id}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={(key, dir) => { setSortKey(key); setSortDirection(dir); }}
          onRowClick={(r) => alert(`Kliknięto zamówienie ${r.number}`)}
          loading={loading}
          striped
        />
      </Subsection>

      <Subsection
        n={2}
        title="Wariant z zaznaczeniem (bulk actions)"
        description={<>Kolumna <code className="px-1 rounded bg-surface-raised text-[10px]">__select</code> jako pierwsza, szerokość 40px, align <code className="px-1 rounded bg-surface-raised text-[10px]">center</code>. Nagłówek kolumny renderuje checkbox „select all".</>}
        actions={
          <span className="text-xs text-text-muted">
            Zaznaczono: <strong className="text-accent-primary">{selectedIds.size}</strong> / {sorted.length}
          </span>
        }
      >
        <Table
          columns={columnsWithSelection}
          data={sorted}
          getRowKey={(r) => r.id}
          onRowClick={(r) => toggleRow(r.id)}
        />
      </Subsection>

      <Subsection n={3} title="Wariant compact + empty state">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Table
            columns={columns}
            data={ORDER_DATA.slice(0, 3)}
            getRowKey={(r) => r.id}
            compact
          />
          <Table
            columns={columns}
            data={[]}
            getRowKey={(r) => r.id}
            emptyMessage="Nie znaleziono zamówień pasujących do filtrów"
          />
        </div>
      </Subsection>

      <Subsection n={4} title="DataList (key-value pairs)">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card variant="elevated" header={<h4 className="text-lg font-semibold text-text-primary">Szczegóły faktury</h4>}>
            <DataList items={invoiceItems} />
          </Card>
          <Card variant="default" header={<h4 className="text-lg font-semibold text-text-primary">Vertical + dense</h4>}>
            <DataList
              orientation="vertical"
              dense
              dividers={false}
              items={[
                { label: 'Adres dostawy', value: 'ul. Marszałkowska 1, 00-001 Warszawa' },
                { label: 'Email kontaktowy', value: 'anna.nowak@example.com' },
                { label: 'Telefon', value: '+48 600 000 000' },
                { label: 'Notatka', value: 'Proszę zostawić pod drzwiami.' },
              ]}
            />
          </Card>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Progress bar
// ============================================================================

export function ProgressBarSection() {
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<ProgressBar>'}</code>{' '}
        — horyzontalny pasek postępu. Track = surface-muted (widoczny w obu
        motywach), fill = gradient accent default albo solid status color
        (success/warning/danger/info). Wzór z FinanceDashboardPage per-sklep
        progress vs cel.
      </p>

      <CodeSnippet
        language="tsx"
        code={`import { ProgressBar } from '@/components/ui/ProgressBar';

<ProgressBar value={55} showPercent label="Cel miesięczny" />
<ProgressBar value={72} tone="success" size="sm" />
<ProgressBar value={18} tone="danger" size="lg" />`}
      />

      <Subsection n={1} title="Warianty tone">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-5 space-y-4">
          <ProgressBar value={55} tone="accent" label="Accent (gradient, default)" showPercent />
          <ProgressBar value={72} tone="success" label="Success" showPercent />
          <ProgressBar value={38} tone="warning" label="Warning" showPercent />
          <ProgressBar value={12} tone="danger" label="Danger" showPercent />
          <ProgressBar value={84} tone="info" label="Info" showPercent />
        </div>
      </Subsection>

      <Subsection n={2} title="Rozmiary">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-5 space-y-4">
          <ProgressBar value={60} size="sm" label="Small (h-1.5)" showPercent />
          <ProgressBar value={60} size="md" label="Medium — default (h-2)" showPercent />
          <ProgressBar value={60} size="lg" label="Large (h-3)" showPercent />
        </div>
      </Subsection>

      <Subsection n={3} title="Bez labela / custom layout">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-5 space-y-4">
          <div>
            <div className="text-xs text-text-muted mb-1.5">Sam pasek (bez label/percent):</div>
            <ProgressBar value={45} />
          </div>
          <div>
            <div className="text-xs text-text-muted mb-1.5">Z custom label (ReactNode):</div>
            <ProgressBar
              value={87}
              label={<span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-success" /> <span className="font-medium text-text-primary">Cel osiągnięty</span></span>}
              showPercent
            />
          </div>
        </div>
      </Subsection>
    </div>
  );
}

/** Prosty helper - sortowanie in-place po wartości. Dla MVP wystarczy. */
function useMemoSorted<T>(data: T[], sortKey?: string, direction: SortDirection = 'asc'): T[] {
  if (!sortKey) return data;
  const copy = [...data];
  copy.sort((a, b) => {
    const av = (a as Record<string, unknown>)[sortKey];
    const bv = (b as Record<string, unknown>)[sortKey];
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return direction === 'asc' ? av - bv : bv - av;
    const as = String(av);
    const bs = String(bv);
    return direction === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
  });
  return copy;
}
